import {isRouteErrorResponse, Link, useRouteError} from "react-router-dom"
import {Stack, Typography, useTheme} from "@mui/joy"
import {Home, RotateCw} from "lucide-react"
import {useTranslation} from "react-i18next"
import Button from "../components/Button"
import FullLogo from "../components/Logo"
import {activePalette, applyTransparency} from "../theme"

export type ErrorPageProps = {
    /** Forced by the catch-all route; left out, the page reads the error the router caught */
    code?: 404 | 500
}

const COPY_KEYS = {
    404: {title: "notFoundTitle", description: "notFoundDescription"},
    500: {title: "serverErrorTitle", description: "serverErrorDescription"}
}

/**
 * What the app shows for an address it has no screen for, and for a screen that crashed.
 *
 * Rendered outside the layout on purpose: the error may come from the layout itself, so the page
 * depends on nothing but the theme and the translations.
 */
const ErrorPage = ({code}: ErrorPageProps) => {
    const {t} = useTranslation()
    const palette = activePalette(useTheme())
    const error = useRouteError()
    const status = code ?? (isRouteErrorResponse(error) && error.status === 404 ? 404 : 500)
    const copy = COPY_KEYS[status]

    if (status === 500 && error)
        console.error("Route error: ", error)

    return <Stack minHeight={"100dvh"} alignItems={"center"} justifyContent={"center"} gap={3}
                  paddingX={2} paddingY={4} boxSizing={"border-box"} textAlign={"center"}
                  sx={{
                      backgroundColor: "#050e1c",
                      backgroundImage: `radial-gradient(circle at 50% 30%, ${applyTransparency(palette.primary["500"], 0.25)}, transparent 55%)`
                  }}>
        <FullLogo width={140}/>

        <Stack gap={1} maxWidth={"28rem"}>
            <Typography level={"h1"} textColor={"primary.700"}
                        sx={{fontFamily: "'Orbitron', sans-serif", fontSize: {xs: "4rem", sm: "5.5rem"}, lineHeight: 1}}>
                {status}
            </Typography>
            <Typography level={"h3"}>{t(copy.title)}</Typography>
            <Typography level={"body-sm"}>{t(copy.description)}</Typography>
        </Stack>

        <Stack direction={{xs: "column", sm: "row"}} gap={1.5} width={{xs: "100%", sm: "auto"}} maxWidth={"20rem"}>
            {status === 500 && <Button variant={"outlined"} startDecorator={<RotateCw size={18}/>}
                                       onClick={() => window.location.reload()}>
                {t("reloadPage")}
            </Button>}
            {/* A full load rather than a client navigation: after a crash the app state is not to be trusted */}
            <Link to={"/"} reloadDocument={status === 500} style={{textDecoration: "none"}}>
                <Button startDecorator={<Home size={18}/>} sx={{width: "100%"}}>{t("backHome")}</Button>
            </Link>
        </Stack>
    </Stack>
}

export default ErrorPage
